import { createContext, useContext, useEffect, useRef, useState } from 'react'

/**
 * An answer imposed from outside, for anything that needs to see both halves of
 * a device-dependent control without owning the device. Null - the only value
 * the app itself ever supplies - means ask the browser. Storybook provides it so
 * the hold and the two-step click can be compared on one screen.
 */
export const PointerOverride = createContext<boolean | null>(null)

/** The query that decides it, in one place: a pointer that can hover and aim. */
const FINE_POINTER = '(hover: hover) and (pointer: fine)'

/**
 * True on a mouse-and-keyboard device. Two controls read this and change what
 * they are, so it has to be a render-time fact rather than something worked out
 * from the pointer event that arrives - a button cannot label itself "click
 * again to confirm" after the click it is describing.
 *
 * A media query rather than `PointerEvent.pointerType` on purpose. A laptop
 * with a touchscreen reports both, and what matters is not which device sent
 * this one event but which one the person is sitting behind; `hover` is the
 * half of the query that answers that. It is also the difference between the
 * two paths being testable - a browser automating a phone emulation drives it
 * with synthetic mouse events, and a pointerType check would put every one of
 * those on the desktop path.
 */
export function useFinePointer(): boolean {
  const forced = useContext(PointerOverride)
  const [fine, setFine] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(FINE_POINTER).matches,
  )

  useEffect(() => {
    const query = window.matchMedia(FINE_POINTER)
    const sync = () => setFine(query.matches)
    sync()
    query.addEventListener('change', sync)
    return () => query.removeEventListener('change', sync)
  }, [])

  return forced ?? fine
}

/**
 * Keeps the screen on while the game is live. Discussion rounds run for many
 * minutes and a locked phone means a missed turn. Requires a secure context,
 * which the deployment provides; it degrades silently everywhere else.
 */
export function useWakeLock(active: boolean): void {
  const lock = useRef<WakeLockSentinel | null>(null)

  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return
    let cancelled = false

    const acquire = async () => {
      try {
        const sentinel = await navigator.wakeLock.request('screen')
        if (cancelled) {
          void sentinel.release()
          return
        }
        lock.current = sentinel
      } catch {
        // Denied, unsupported, or the tab is in the background. Not fatal.
      }
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') void acquire()
    }

    void acquire()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
      void lock.current?.release()
      lock.current = null
    }
  }, [active])
}

/**
 * A buzz, wherever the app has something to say with the phone itself. One
 * place, because the swallow is the point: `vibrate` is unsupported on iOS
 * Safari and throws behind some permission policies, and neither is a reason
 * for a caller to stop what it was doing.
 */
export function pulse(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern)
  } catch {
    // Unsupported. Whatever is on screen carries the alert instead.
  }
}
